/**
 * Guarda resultados en un ensayo cuando hay código activo (?cod= o sesión local).
 * En el sitio público (sin ensayo) no muestra nada: el botón "Guardar mis resultados"
 * se ocultó a propósito; el guardado de ensayos debe ser automático (como el ASP.NET).
 *
 * Props:
 *  tipo      — string identificador del test (ej. "LT82", "R5")
 *  registros — array: { pregunta, respuesta, respuestaUsuario, correcta, tiempoRespuesta }
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  guardarResultado,
  getSesionLocal,
  setSesionLocal,
  resolveEnsayoCode,
} from '../../../api/ensayosClient';

const STATE = { IDLE: 'idle', SAVING: 'saving', SAVED: 'saved', ERROR: 'error' };

export default function SaveResults({ tipo, registros }) {
  const [params] = useSearchParams();
  const codFromUrl = (params.get('cod') ?? '').trim().toUpperCase();
  const code = resolveEnsayoCode(codFromUrl);

  const [state, setState] = useState(STATE.IDLE);
  const [error, setError] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (!code || !tipo || started.current) return;
    started.current = true;

    const sesion = getSesionLocal();
    if (!sesion || sesion.code !== code) {
      setSesionLocal(code, sesion?.sujeto ?? '');
    }

    setState(STATE.SAVING);
    setError('');
    guardarResultado(code, tipo, registros ?? [])
      .then(() => setState(STATE.SAVED))
      .catch((e) => {
        setError(e.message || 'No se pudieron guardar los resultados');
        setState(STATE.ERROR);
      });
    // Solo al montar con código de ensayo; `registros` ya está completo en DONE.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, tipo]);

  if (!code) return null;

  if (state === STATE.SAVED) {
    return (
      <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center text-sm text-emerald-700">
        Resultados guardados en el ensayo.
      </div>
    );
  }

  if (state === STATE.ERROR) {
    return (
      <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 text-center text-sm text-red-700">
        {error || 'Error al guardar los resultados del ensayo.'}
      </div>
    );
  }

  if (state === STATE.SAVING) {
    return (
      <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-center text-sm text-slate-600">
        Guardando resultados del ensayo...
      </div>
    );
  }

  return null;
}
